import { useState, useEffect } from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { ChatFeedbackModal } from "./ChatFeedbackModal";

interface ChatFeedbackButtonsProps {
  messageId: string;
  messageStatus: string;
  prompt: string;
  response: string;
  userId: string;
  assistantType: "projects" | "correspondence" | "spaces";
  conversationId?: string;
  spanId?: string;
}

export function ChatFeedbackButtons({
  messageId,
  messageStatus,
  prompt,
  response,
  userId,
  assistantType,
  conversationId,
  spanId,
}: ChatFeedbackButtonsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preselectedRating, setPreselectedRating] = useState<number>(0);
  const [submittedRating, setSubmittedRating] = useState<number>(0);
  const [showRatingInterface, setShowRatingInterface] = useState(false);

  // Show rating interface with delay after message completes
  useEffect(() => {
    if (messageStatus === "completed") {
      const timer = setTimeout(() => {
        setShowRatingInterface(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [messageStatus]);

  const handleRatingClick = (rating: number) => {
    if (submittedRating > 0) return;
    setPreselectedRating(rating);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPreselectedRating(0);
  };

  const handleFeedbackSubmitted = (rating: number) => {
    setSubmittedRating(rating);
    handleModalClose();
  };

  if (messageStatus !== "completed") return null;

  return (
    <div
      className={`flex gap-1 mt-2 items-center transition-all duration-500 ease-out ${
        showRatingInterface
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2"
      }`}
    >
      <span className="text-xs text-gray-500">Rate this response:</span>

      {/* Not Good */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={`group size-7 rounded-full flex items-center justify-center transition-all duration-200 ${
              submittedRating > 0
                ? "cursor-not-allowed"
                : "cursor-pointer hover:bg-gray-200"
            }`}
            onClick={() => handleRatingClick(1)}
            disabled={submittedRating > 0}
          >
            <span
              className={`text-sm transition-all duration-200 ${
                submittedRating === 1
                  ? "grayscale-0"
                  : submittedRating > 0
                  ? "grayscale opacity-50"
                  : "grayscale group-hover:grayscale-0"
              }`}
            >
              {"\u{1F615}"}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {submittedRating > 0 ? "Feedback submitted" : "Not Good"}
        </TooltipContent>
      </Tooltip>

      {/* Good */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={`group size-7 rounded-full flex items-center justify-center transition-all duration-200 ${
              submittedRating > 0
                ? "cursor-not-allowed"
                : "cursor-pointer hover:bg-gray-200"
            }`}
            onClick={() => handleRatingClick(5)}
            disabled={submittedRating > 0}
          >
            <span
              className={`text-sm transition-all duration-200 ${
                submittedRating === 5
                  ? "grayscale-0"
                  : submittedRating > 0
                  ? "grayscale opacity-50"
                  : "grayscale group-hover:grayscale-0"
              }`}
            >
              {"\u{1F60A}"}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {submittedRating > 0 ? "Feedback submitted" : "Good"}
        </TooltipContent>
      </Tooltip>

      {submittedRating > 0 && (
        <span className="text-xs text-green-600 ml-1">Submitted!</span>
      )}

      <ChatFeedbackModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        messageId={messageId}
        prompt={prompt}
        response={response}
        preselectedRating={preselectedRating}
        onFeedbackSubmitted={handleFeedbackSubmitted}
        assistantType={assistantType}
        conversationId={conversationId}
        userId={userId}
        spanId={spanId}
      />
    </div>
  );
}
